import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";

import { API_BASE_URL } from "@/lib/apiBase";
const getHandoffId = (h) => h?.handoff_id ?? h?.handoffId;
const getFromTeamName = (h) =>
  h?.fromTeam?.name || h?.from_team?.name || `Team ${h?.fromTeamId ?? h?.from_team_id}`;
const getPostTitle = (h) => h?.post?.title || `Post #${h?.postId ?? h?.post_id}`;
const getItemCount = (h) => Number(h?.item_count ?? h?.itemCount ?? 1) || 1;
const getWorkKind = (h) =>
  h?.work_kind || h?.workKind || h?.post?.work_kind || h?.post?.workKind || "task";
const batchHeadline = (h) => {
  const count = getItemCount(h);
  const title = getPostTitle(h);
  if (count <= 1) return title;
  const kind = getWorkKind(h);
  const label =
    kind === "epic" ? "Epic" : kind === "story" ? "Story" : "Work";
  return `${label} · ${title} · ${count} items`;
};

const InboundHandoffsPanel = ({
  open,
  onClose,
  teamId,
  orgId,
  accessToken,
  lanes = [],
  onChanged,
}) => {
  const [handoffs, setHandoffs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [acceptingId, setAcceptingId] = useState(null);
  const [targetLane, setTargetLane] = useState("");
  const [busyId, setBusyId] = useState(null);

  const sortedLanes = [...lanes].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0)
  );

  const loadInbound = useCallback(async () => {
    if (!accessToken || !teamId || !orgId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/posts/handoffs/inbound`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: {
          team_id: teamId,
          org_id: orgId,
          status: "pending",
        },
      });
      setHandoffs(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Failed to load inbound handoffs:", err);
      setError(err.response?.data?.message || "Failed to load inbound handoffs");
      setHandoffs([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken, teamId, orgId]);

  useEffect(() => {
    if (!open) {
      setAcceptingId(null);
      setTargetLane("");
      setError(null);
      return;
    }
    loadInbound();
  }, [open, loadInbound]);

  const refreshParent = async () => {
    if (onChanged) await onChanged();
  };

  const handleAccept = async (handoff) => {
    const id = getHandoffId(handoff);
    if (!targetLane) {
      setError("Select a destination lane");
      return;
    }
    setBusyId(id);
    setError(null);
    try {
      await axios.post(
        `${API_BASE_URL}/posts/handoffs/${id}/accept`,
        { target_lane: targetLane },
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { org_id: orgId },
        }
      );
      setAcceptingId(null);
      setTargetLane("");
      await loadInbound();
      await refreshParent();
    } catch (err) {
      console.error("Failed to accept handoff:", err);
      setError(err.response?.data?.message || "Failed to accept handoff");
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (handoff) => {
    const id = getHandoffId(handoff);
    setBusyId(id);
    setError(null);
    try {
      await axios.post(
        `${API_BASE_URL}/posts/handoffs/${id}/reject`,
        {},
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { org_id: orgId },
        }
      );
      if (acceptingId === id) {
        setAcceptingId(null);
        setTargetLane("");
      }
      await loadInbound();
      await refreshParent();
    } catch (err) {
      console.error("Failed to reject handoff:", err);
      setError(err.response?.data?.message || "Failed to reject handoff");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Inbound handoffs</DialogTitle>
          <DialogDescription>
            Pending cards other teams want to hand off to this team.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : handoffs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No pending inbound handoffs.</p>
          ) : (
            <ul className="space-y-3">
              {handoffs.map((h) => {
                const id = getHandoffId(h);
                const isAccepting = acceptingId === id;
                const isBusy = busyId === id;
                return (
                  <li
                    key={id}
                    className="rounded-md border border-border bg-muted/30 p-3 space-y-2"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">
                          {batchHeadline(h)}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          From {getFromTeamName(h)}
                          {getItemCount(h) > 1
                            ? ` · all ${getItemCount(h)} cards move to the same lane`
                            : ""}
                        </p>
                      </div>
                      <Badge variant="secondary" className="shrink-0 text-xs">
                        pending
                      </Badge>
                    </div>
                    {getItemCount(h) > 1 && Array.isArray(h.members) && (
                      <ul className="text-xs text-muted-foreground space-y-0.5 max-h-24 overflow-y-auto pl-1">
                        {h.members.map((m) => (
                          <li key={m.post_id || m.handoff_id}>
                            {(m.work_kind || "task").replace(/^./, (c) =>
                              c.toUpperCase()
                            )}
                            : {m.title || `Post #${m.post_id}`}
                          </li>
                        ))}
                      </ul>
                    )}
                    {h.note && (
                      <p className="text-xs text-muted-foreground whitespace-pre-wrap">
                        {h.note}
                      </p>
                    )}

                    {isAccepting ? (
                      <div className="space-y-2 pt-1">
                        <Label htmlFor={`accept-lane-${id}`}>Destination lane</Label>
                        <select
                          id={`accept-lane-${id}`}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          value={targetLane}
                          onChange={(e) => setTargetLane(e.target.value)}
                          disabled={isBusy}
                        >
                          <option value="">Select lane</option>
                          {sortedLanes.map((lane) => (
                            <option key={lane.lane_id || lane.name} value={lane.name}>
                              {lane.name}
                            </option>
                          ))}
                        </select>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleAccept(h)}
                            disabled={isBusy || !targetLane}
                          >
                            {isBusy ? "Accepting…" : "Confirm accept"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setAcceptingId(null);
                              setTargetLane("");
                            }}
                            disabled={isBusy}
                          >
                            Back
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-2 pt-1">
                        <Button
                          size="sm"
                          onClick={() => {
                            setAcceptingId(id);
                            setTargetLane(
                              sortedLanes[0]?.name || ""
                            );
                            setError(null);
                          }}
                          disabled={isBusy}
                        >
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleReject(h)}
                          disabled={isBusy}
                        >
                          {isBusy ? "…" : "Reject"}
                        </Button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InboundHandoffsPanel;
